import { IParsedChartDataRecord } from '../../components/Chart/interfaces';
import { useMemo } from 'react';
import { assertCondition } from '../types';
import { DataKey } from 'recharts/types/util/types';
import { getValueFromDataPoint } from '../redux/util';

/**
 * Recharts' performance drops vastly when a single data record contains many keys
 * We usually have many series in each record (main value, percent value, etc)
 *
 * Luckily, Recharts allow us to specify a _function_ (see DataKey interface)
 * which extracts the value for each data point. This way we can avoid passing
 * a huge record to it.
 *
 * To achieve this, we create a bare-bones IParsedChartDataRecord[] which only
 * contains { [xAxisKey]: value } (xAxisKey is usually "date", but see FunnelXAxisKey)
 * Then, we create function that extracts the value from the complete
 * IParsedChartDataRecord[] (available in the closure).
 * We use high-order function (dataKeyCreator) to simplify the interfaces
 * (at use side at least): based on the passed series key
 * (also identified by DataKey), create a DataKey (another function)
 * which can extract the correct value based on the xAxis value (day)
 */
export const useOptimizedChartData = (
  dataRecords: IParsedChartDataRecord[],
  xAxisKey: string,
) => {
  return useMemo(() => {
    const xAxisSeries: IParsedChartDataRecord[] = [];
    // quick lookup by xAxis value (e.g. '5/30/2022') to
    // the index where it occurs (e.g. 0) in the dataRecords
    const xAxisIndices = new Map<string, number>();

    for (let index = 0; index < dataRecords.length; index++) {
      const record = dataRecords[index];
      const xAxisValue = record[xAxisKey];
      assertCondition(typeof xAxisValue === 'string');
      xAxisSeries.push({ [xAxisKey]: xAxisValue });
      xAxisIndices.set(xAxisValue, index);
    }

    const yAxisDataKeyCreator =
      (
        yAxisDataKey: DataKey<IParsedChartDataRecord>,
      ): DataKey<IParsedChartDataRecord> =>
      (xAxisRecord: IParsedChartDataRecord) => {
        const xAxisValue = xAxisRecord[xAxisKey];
        assertCondition(typeof xAxisValue === 'string');
        const xAxisIndex = xAxisIndices.get(xAxisValue);

        if (xAxisIndex === undefined) {
          return null;
        }

        const value = getValueFromDataPoint(
          dataRecords[xAxisIndex],
          yAxisDataKey,
        );
        if (value === undefined) {
          return null;
        }
        return value;
      };

    return {
      xAxisKey,
      xAxisSeries,
      yAxisDataKeyCreator,
    };
  }, [dataRecords, xAxisKey]);
};
